import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchConditions } from "../../Redux/WebsiteSlice";
import { useBookingModal } from "../../context/BookingModalContext";
import "./ConditionsPage.scss";

const defaultConditions = [
  {
    title: "Back Pain",
    description:
      "Lower and upper back pain is one of the most common musculoskeletal conditions. Our physiotherapists use hands-on treatment, exercise therapy, and education to address the root cause.",
  },
  {
    title: "Neck Pain",
    description:
      "Neck pain can result from poor posture, injury, or degeneration. We provide targeted treatment including joint mobilisation, soft tissue therapy, and personalised exercise programs.",
  },
  {
    title: "Shoulder Pain",
    description:
      "From rotator cuff tears to frozen shoulder, our team is experienced in diagnosing and treating the full range of shoulder conditions to restore strength and movement.",
  },
  {
    title: "Knee Pain",
    description:
      "Whether you have osteoarthritis, a ligament injury, or runner's knee, our physiotherapists can help you reduce pain and get back to the activities you love.",
  },
  {
    title: "Hip Pain",
    description:
      "Hip pain can significantly affect mobility and quality of life. We treat hip conditions including bursitis, labral tears, and post-surgical rehabilitation.",
  },
  {
    title: "Sports Injuries",
    description:
      "Sprains, strains, and overuse injuries are common in athletes of all levels. Our sports physiotherapists design structured return-to-sport programs tailored to your goals.",
  },
  {
    title: "Post-Surgical Rehabilitation",
    description:
      "Recovery after surgery requires expert guidance. We work closely with surgeons to provide evidence-based rehabilitation programs that optimise your recovery.",
  },
  {
    title: "Headaches & Migraines",
    description:
      "Cervicogenic headaches originating from the neck respond well to physiotherapy. We assess and treat the underlying cervical dysfunction contributing to your headaches.",
  },
  {
    title: "Arthritis",
    description:
      "Exercise and hands-on therapy are effective in managing arthritis symptoms. We help patients maintain function, reduce pain, and improve their quality of life.",
  },
  {
    title: "Sciatica",
    description:
      "Sciatica causes pain radiating down the leg and can be debilitating. Our treatment addresses the spinal cause and helps relieve nerve pain through targeted therapy.",
  },
  {
    title: "Foot & Ankle Pain",
    description:
      "From plantar fasciitis to ankle sprains, our physiotherapists provide effective assessment and treatment for all foot and ankle conditions.",
  },
  {
    title: "NDIS & Chronic Conditions",
    description:
      "We support NDIS participants and people with chronic conditions through tailored exercise therapy and physiotherapy programs to maximise independence and wellbeing.",
  },
];

const icons = [
  <svg key="back" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a7 7 0 0 1 7 7v6l2 3H3l2-3V9a7 7 0 0 1 7-7z"/></svg>,
  <svg key="neck" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="5" r="3"/><path d="M12 8v8M8 18h8"/></svg>,
  <svg key="shoulder" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12h16M12 4l-8 8 8 8 8-8-8-8z"/></svg>,
  <svg key="knee" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20M5 8l7-6 7 6M5 16l7 6 7-6"/></svg>,
  <svg key="hip" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><ellipse cx="12" cy="12" rx="9" ry="6"/><line x1="12" y1="6" x2="12" y2="18"/></svg>,
  <svg key="sports" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M4.93 4.93l4.24 4.24M14.83 14.83l4.24 4.24M14.83 9.17l4.24-4.24M4.93 19.07l4.24-4.24"/></svg>,
];

const ConditionsPage = () => {
  const dispatch = useDispatch();
  const conditions = useSelector((state) => state.website.conditions);
  const { openModal } = useBookingModal();

  useEffect(() => {
    dispatch(fetchConditions());
  }, [dispatch]);

  const displayConditions = conditions && conditions.length > 0 ? conditions : defaultConditions;

  return (
    <div className="page-conditions">
      <div className="page-hero">
        <div className="page-hero__content">
          <span className="small__text">WE TREAT</span>
          <h1 className="headline__text">
            Common <span>Conditions</span>
          </h1>
          <p className="page-hero__subtitle">
            Our physiotherapists are experienced in treating a wide range of musculoskeletal and chronic conditions.
          </p>
        </div>
      </div>

      <div className="conditions-intro">
        <div className="conditions-intro__inner">
          <p>
            At Integrity Physio, we treat patients of all ages and activity levels. Whether you're recovering from an injury, managing a chronic condition, or looking to improve your function and performance — our team is here to help.
          </p>
        </div>
      </div>

      <div className="conditions-grid-section">
        <div className="conditions-grid-section__inner">
          <div className="conditions-grid">
            {displayConditions.map((condition, i) => (
              <div key={condition._id || i} className="condition-card">
                <div className="condition-card__icon">
                  {icons[i % icons.length]}
                </div>
                <h3 className="condition-card__title">{condition.title}</h3>
                {condition.description && (
                  <p className="condition-card__desc">{condition.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="conditions-cta">
        <div className="conditions-cta__inner">
          <span className="small__text">NOT SURE WHERE TO START?</span>
          <h2>Book a Physiotherapy Assessment</h2>
          <p>
            Our physiotherapists will assess your condition, diagnose the problem, and create a personalised treatment plan.
          </p>
          <button
            type="button"
            onClick={() => setTimeout(openModal, 0)}
            className="site__button site__button_style--solid site__button_shape--default"
          >
            <span className="text">Book an Appointment</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConditionsPage;
